import { useState } from 'react';

import { useForm } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { removeUserAuthData } from 'utils/login';
import { EToasterStatus } from 'utils/showToast';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import { EModalState } from 'components/LoginModal/LoginModal.types';
import Typography from 'components/Typography/Typography';
import { Form, FormControl, FormField, FormItem } from 'components/ui';

import { GOOGLE_AUTH_CLIENT_ID } from '../../constants/constants';

import { DEFAULT_VALUES, LOGIN_FORM_SCHEMA, LoginFormData } from './constants';
import { ZENDESK_DOMAIN } from './constants';
import { GoogleLogin } from './GoogleLogin/GoogleLogin';
import { LoginFormProps } from './LoginForm.types';
import { MicrosoftLogin } from './MicrosoftLogin/MicrosoftLogin';

const LoginForm = ({
  login,
  setLoginNotification,
  setModalState
}: LoginFormProps) => {
  // Get Zendesk parameters from query params
  const searchParams = new URLSearchParams(window.location.search);
  const origin = searchParams.get('brand_id') || '';
  const returnTo = searchParams.get('return_to') || '';
  const isZendesk = !!origin || window.location.hostname === ZENDESK_DOMAIN;

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(LOGIN_FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      ...DEFAULT_VALUES,
      isZendesk
    }
  });

  const {
    formState: { errors, isValid, isDirty }
  } = form;

  const onLoginFailed = () => {
    setLoginNotification({
      message: 'There was a problem with your login credentials, please try again.',
      type: EToasterStatus.ERROR
    });
    removeUserAuthData();
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    const formattedUserName = data.userName.toLowerCase().trim();

    login(
      {
        userName: formattedUserName,
        password: data.password,
        rememberMe: data.rememberMe,
        isZendesk,
        returnTo
      },
      (success: boolean) => {
        setIsLoading(false);
        if (!success) {
          onLoginFailed();
        }
      }
    );
  };

  const handleFormSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  const isSubmitButtonEnabled = isValid && isDirty && !isLoading;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_AUTH_CLIENT_ID}>
      <Form {...form}>
        <GoogleLogin login={login} onLoginFailed={onLoginFailed} />
        <MicrosoftLogin login={login} onLoginFailed={onLoginFailed} />
        <div className="flex items-center justify-center gap-x-5 my-[22px] w-full text-p-xs text-base-mutedForeground">
          <hr className="w-full h-[1px] bg-base-sidebarBorder" />
          OR
          <hr className="w-full h-[1px] bg-base-sidebarBorder" />
        </div>
        <form
          autoComplete="off"
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-y-4 w-full"
        >
          <FormField
            control={form.control}
            name="userName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    isRequired
                    label="Email"
                    placeholder="Enter your email"
                    state={errors.userName?.message ? 'error' : 'default'}
                    description={errors.userName?.message}
                    autoComplete="username"
                    onKeyDown={(e: any) => {
                      if (e.key === 'Enter') {
                        handleFormSubmit();
                      }
                    }}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input
                      rightIcon={
                        <FontAwesomeIcon
                          onClick={() => setShowPassword((prev) => !prev)}
                          icon={
                            showPassword
                              ? 'fa-solid fa-eye'
                              : 'fa-solid fa-eye-slash'
                          }
                        />
                      }
                      type={showPassword ? 'text' : 'password'}
                      isRequired
                      label="Password"
                      placeholder="Password"
                      state={errors.password?.message ? 'error' : 'default'}
                      description={errors.password?.message}
                      autoComplete="current-password"
                      onKeyDown={(e: any) => {
                        if (e.key === 'Enter') {
                          handleFormSubmit();
                        }
                      }}
                      {...field}
                    />
                    <Typography
                      tag="span"
                      className="absolute right-0 top-0 cursor-pointer hover:opacity-80 underline text-p-s text-base-mutedForeground"
                      onClick={() => setModalState(EModalState.FORGET_PASSWORD)}
                    >
                      Forgot Password?
                    </Typography>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <Button
            disabled={!isSubmitButtonEnabled}
            onClick={handleFormSubmit}
            className="w-full"
            isLoading={isLoading}
          >
            Login
          </Button>
        </form>
      </Form>
    </GoogleOAuthProvider>
  );
};

export default LoginForm;
